import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authApi } from '../api/auth';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/errors';
import { LoginError } from './loginError';

const ADMIN_REQUIRED_MESSAGE = 'Нет доступа: нужна роль администратора';


interface User {
  id: string;
  email: string | null;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: 'user' | 'admin';
  is_anonymous: boolean;
  isSubscribed: boolean;
  subscribedEnd: Date | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (loginIdentifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth state is driven by httpOnly cookies set by backend on login.
 * Client never reads/writes tokens; we only call /auth/me to discover
 * who's logged in, and rely on the apiClient interceptor to refresh on 401.
 *
 * Non-sensitive user profile is mirrored in localStorage so the UI can
 * paint instantly on reload while we revalidate via /auth/me.
 */
const USER_PROFILE_KEY = 'admin_user_profile';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem(USER_PROFILE_KEY);
      return cached ? (JSON.parse(cached) as User) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  const persistUser = (u: User | null) => {
    setUser(u);
    if (u) {
      // Store only minimal fields for instant UI paint on reload.
      const minimal = {
        id: u.id,
        username: u.username,
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.role,
      } as Partial<User>;
      try {
        localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(minimal));
      } catch {
        // ignore quota errors
      }
    } else {
      localStorage.removeItem(USER_PROFILE_KEY);
    }
  };

  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.getCurrentUser();
      if (response.user.role !== 'admin') {
        toast.error(ADMIN_REQUIRED_MESSAGE);
        await doLogout(false);
        return;
      }
      persistUser(response.user as User);
    } catch (error: any) {
      // 401 → interceptor already attempted refresh and either succeeded
      // (we'll get re-called) or redirected to login. Just treat as logged out.
      if (error?.response?.status === 401) {
        persistUser(null);
      } else {
        // Network or other error — keep cached user, don't surface.
        // Login flow will re-validate.
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (loginIdentifier: string, password: string) => {
    try {
      const response = await authApi.login({ loginIdentifier, password });
      // Backend has set httpOnly auth cookies + non-httpOnly csrf cookie.
      if (response.user.role !== 'admin') {
        // Wipe cookies via /auth/logout; admin role missing.
        await authApi.logout().catch(() => {});
        throw new Error(ADMIN_REQUIRED_MESSAGE);
      }
      persistUser(response.user as User);
      toast.success('Вход выполнен');
    } catch (error: any) {
      const errorMessage =
        error instanceof Error && error.message === ADMIN_REQUIRED_MESSAGE
          ? ADMIN_REQUIRED_MESSAGE
          : getErrorMessage(error, 'Не удалось войти');
      // Ошибку показывает форма входа (под полем пароля), без дублирующего тоста.
      throw new LoginError(errorMessage, error);
    }
  };

  const doLogout = async (notify: boolean) => {
    try {
      await authApi.logout();
    } catch {
      // ignore — server-side logout is best-effort
    }
    persistUser(null);
    if (notify) toast.success('Вы вышли из админки');
  };

  const logout = async () => {
    await doLogout(true);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
